import { fetchInsuredOrderDetails } from "@/modules/Quote/api/quoteApi.js";
import { logApiError } from "@/shared/services/config.js";
import paymentOrderStore from "@/modules/Payment/store/paymentOrderStore.js";
import submissionStore from "@/modules/Quote/store/submissionStore.js";

// In-flight promise per submission id — when two callers (e.g. Resume
// handler + LicenseScopePage mount effect) ask for the same order at the
// same time, they share one network request instead of each firing its
// own GET /insured/order.
const inflightBySid = new Map();

/**
 * Re-fetch GET /insured/order for the active submission and write the
 * result into paymentOrderStore. Used by every page that needs the
 * authoritative `paymentOrderDetails.policyStatus` (Review, Payment,
 * UnderwritingPage save flow, etc.).
 *
 * @param {string|number} submissionId
 * @param {{ ifMissing?: boolean }} [opts]
 *   `ifMissing: true` → no-op when the store already holds the order for
 *   this sid. Mount-time effects pass this so we don't re-fetch after
 *   the Resume handler just loaded it. Save flows pass nothing to force
 *   a refresh.
 */
export async function refreshPaymentOrder(submissionId, { ifMissing = false } = {}) {
  // D4 guard — reject non-positive / unparseable ids before they reach the
  // backend (a `submissionId=0` would 404 and surface a confusing error).
  const sidNum = Number(submissionId);
  if (!Number.isFinite(sidNum) || sidNum <= 0) return null;
  const key = String(submissionId);
  if (ifMissing
    && paymentOrderStore.loadedSubmissionId === key
    && paymentOrderStore.paymentOrderDetails) {
    return paymentOrderStore.paymentOrderDetails;
  }
  if (inflightBySid.has(key)) return inflightBySid.get(key);

  paymentOrderStore.paymentOrderLoading = true;
  paymentOrderStore.paymentOrderError = null;
  const p = (async () => {
    try {
      const order = await fetchInsuredOrderDetails(submissionId);
      // D3 guard — if the user has navigated away (sid cleared or swapped
      // for a new submission) while this fetch was in flight, drop the
      // result on the floor instead of poisoning the store with stale
      // details. usePolicyStatus.sidMatches would catch this downstream
      // too, but discarding here keeps the store clean.
      const currentSid = submissionStore.flowSubmissionId;
      if (currentSid != null && String(currentSid) !== key) return order;
      paymentOrderStore.paymentOrderDetails = order;
      paymentOrderStore.loadedSubmissionId = key;
      return order;
    } catch (e) {
      logApiError(e);
      paymentOrderStore.paymentOrderError = e?.message || "Could not load order details";
      return null;
    } finally {
      paymentOrderStore.paymentOrderLoading = false;
      inflightBySid.delete(key);
    }
  })();
  inflightBySid.set(key, p);
  return p;
}
